import {useCallback, useEffect, useRef, useState} from 'react';
import {uploadChatAttachment} from '../../../service/ChatService';
import {
    MAX_CHAT_ATTACHMENTS,
    normalizedAttachmentContentType,
    validateChatAttachment,
} from './attachmentRules';

function uploadStatus(stage) {
    if (stage === 'hashing') return 'Preparing…';
    if (stage === 'verifying') return 'Verifying…';
    return 'Uploading…';
}

export default function useAttachmentUploads(conversationId) {
    const [items, setItems] = useState([]);
    const [validationError, setValidationError] = useState(null);
    const itemsRef = useRef([]);
    const controllersRef = useRef(new Map());

    const replaceItems = useCallback(update => {
        setItems(current => {
            const next = typeof update === 'function' ? update(current) : update;
            itemsRef.current = next;
            return next;
        });
    }, []);

    const updateItem = useCallback((localId, update) => {
        replaceItems(current => current.map(item => item.local_id === localId ? {...item, ...update} : item));
    }, [replaceItems]);

    const startUpload = useCallback(item => {
        const controller = new AbortController();
        controllersRef.current.set(item.local_id, controller);
        updateItem(item.local_id, {status: 'uploading', status_label: 'Preparing…', progress: 0, error: null});
        uploadChatAttachment(conversationId, item.file, {
            contentType: item.content_type,
            signal: controller.signal,
            onProgress: ({stage, progress}) => updateItem(item.local_id, {
                status: 'uploading',
                status_label: uploadStatus(stage),
                progress,
            }),
        }).then(attachment => {
            updateItem(item.local_id, {status: 'ready', status_label: 'Ready', progress: 100, attachment});
        }).catch(error => {
            if (error?.name !== 'AbortError') {
                updateItem(item.local_id, {status: 'error', status_label: 'Upload failed', error});
            }
        }).finally(() => controllersRef.current.delete(item.local_id));
    }, [conversationId, updateItem]);

    const addFiles = useCallback(fileList => {
        if (!conversationId) return;
        const incoming = Array.from(fileList || []);
        if (incoming.length === 0) return;
        const available = Math.max(0, MAX_CHAT_ATTACHMENTS - itemsRef.current.length);
        const accepted = [];
        const errors = [];
        incoming.forEach(file => {
            if (accepted.length >= available) {
                errors.push(`A message can contain at most ${MAX_CHAT_ATTACHMENTS} attachments.`);
                return;
            }
            const error = validateChatAttachment(file);
            if (error) {
                errors.push(`${file.name || 'File'}: ${error}`);
                return;
            }
            accepted.push({
                local_id: crypto.randomUUID(),
                file,
                content_type: normalizedAttachmentContentType(file),
                status: 'queued',
                status_label: 'Queued',
                progress: 0,
                error: null,
                attachment: null,
            });
        });
        setValidationError([...new Set(errors)].join(' ') || null);
        if (accepted.length === 0) return;
        replaceItems(current => [...current, ...accepted]);
        accepted.forEach(startUpload);
    }, [conversationId, replaceItems, startUpload]);

    const remove = useCallback(localId => {
        controllersRef.current.get(localId)?.abort();
        controllersRef.current.delete(localId);
        replaceItems(current => current.filter(item => item.local_id !== localId));
        setValidationError(null);
    }, [replaceItems]);

    const retry = useCallback(localId => {
        const item = itemsRef.current.find(candidate => candidate.local_id === localId);
        if (!item) return;
        controllersRef.current.get(localId)?.abort();
        startUpload(item);
    }, [startUpload]);

    const consumeReady = useCallback(() => {
        const ready = itemsRef.current.filter(item => item.status === 'ready').map(item => item.attachment);
        replaceItems([]);
        setValidationError(null);
        return ready;
    }, [replaceItems]);

    useEffect(() => {
        const controllers = controllersRef.current;
        controllers.forEach(controller => controller.abort());
        controllers.clear();
        itemsRef.current = [];
        setItems([]);
        setValidationError(null);
        return () => {
            controllers.forEach(controller => controller.abort());
            controllers.clear();
        };
    }, [conversationId]);

    const allReady = items.length === 0 || items.every(item => item.status === 'ready');
    return {
        items,
        validationError,
        addFiles,
        remove,
        retry,
        consumeReady,
        allReady,
        atLimit: items.length >= MAX_CHAT_ATTACHMENTS,
    };
}
